import { colorIndexFromUint8 } from "@nova/component-color-finder-legend/src/colorspace"
import { useEffect, useMemo, useState } from "react"
import { rgbDownscaledImagePath, rgbImagePath } from "@nova/util-s3"
import { useAppSelector } from "integrations/redux/hooks"
import { useS3VideoUrl } from "plugins/shared/s3url"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { ImageColorFinderFlight, VideoColorFinderFlight } from "@nova/protobuf"
import { localVideoMap, useUniversalControl } from "plugins/shared/useUniversalControl"
import { selectActivatedFireId } from "features/map/control/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { get } from "nodash"
import {
    ColorFinderTiler,
    extractTile,
    LegacyColorFinderTiler,
    ZoomableColorFinderTiler,
} from "./ColorFinderTiler.class"
import { buildTileFetcher, fetchLegacyColorFinderIndex } from "./remote/ColorFinderRemotes"
import { SuperIndexController } from "../processing/SuperIndexController.class"
import { getFrameParser } from "../processing/getFrameParser"
import { decode } from "@msgpack/msgpack"
import { VideoMetadata } from "@nova/slice-video"
import { buildColorIndex } from "./filter/colorspace"
import {
    ColorFinderState,
    localImageColorDataMap,
    selectAllUploads as selectAllColorFinderImageUploads,
    selectUploadsForSet,
} from "../ImageColorFinder/ImageColorFinderSlice"
import { stagedFiles } from "@nova/slice-multifile-upload"
import useSWR, { mutate } from "swr"
import { ColorFinderFootageQuality } from "./redux/slice"
import { useLocalStorageValue } from "tools/store/localStorageRegistry"
import { dateFromNumber } from "@nova/serializer/src/client/util"
import { GlobalNotificationStatus } from "@nova/component-global-notification"

const toDemoKey = (path: string, isDemo: boolean) => {
    if (!isDemo) return path
    const idx = path.indexOf("flight/")
    return `public/sample-data/${idx >= 0 ? path.slice(idx) : path}`
}

const buildVideoTiler = async (flight: VideoColorFinderFlight) => {
    const localIndex = localVideoMap[flight.id]?.colorData
    if (localIndex && localIndex.length) {
        const colorIndex = buildColorIndex(localIndex.at(-1)!)
        return new ZoomableColorFinderTiler(colorIndex.length, (zoom, tileNum) =>
            extractTile(localIndex, zoom, tileNum)
        )
    }

    if (!flight.job) return

    const isDemo = !!flight.demoId
    const colordataKey = toDemoKey(flight.job.colordataPath, isDemo)

    if (isV1ColorFinderVideoFlight(flight)) {
        const index = await fetchLegacyColorFinderIndex(colordataKey, isDemo)
        return new LegacyColorFinderTiler(
            index,
            new SuperIndexController(await getFrameParser(), index.length)
        )
    } else {
        if (!flight.job.numFrames) return

        return new ZoomableColorFinderTiler(
            flight.job.numFrames,
            buildTileFetcher(colordataKey, isDemo)
        )
    }
}

const buildImageTiler = async (flight: ImageColorFinderFlight) => {
    const localIndex = localImageColorDataMap[flight.id]?.colorData
    if (localIndex && localIndex.length) {
        const colorIndex = buildColorIndex(localIndex.at(-1)!)
        return new LegacyColorFinderTiler(
            colorIndex,
            new SuperIndexController(await getFrameParser(), colorIndex.length)
        )
    }
    if (isV1ColorFinderImageFlight(flight)) {
        const colorData = flight.images.map((image) => image.colorData)
        const index = colorIndexFromUint8(...colorData)
        return new LegacyColorFinderTiler(
            index,
            new SuperIndexController(await getFrameParser(), index.length)
        )
    } else {
        if (!flight.colorDataPath) return
        const index = await fetchLegacyColorFinderIndex(flight.colorDataPath, !!flight.demoId)
        return new LegacyColorFinderTiler(
            index,
            new SuperIndexController(await getFrameParser(), index.length)
        )
    }
}

const buildFlightTiler = async (
    flight: VideoColorFinderFlight | ImageColorFinderFlight
): Promise<ColorFinderTiler | undefined> => {
    return isVideoFlight(flight) ? buildVideoTiler(flight) : buildImageTiler(flight)
}

export const invalidateColorTilerCache = () =>
    mutate((key) => Array.isArray(key) && key[0] === "tiler")

export const useColorTiler = (flight: VideoColorFinderFlight | ImageColorFinderFlight) => {
    const { data, isLoading } = useSWR(["tiler", flight.id], () => buildFlightTiler(flight), {
        revalidateOnFocus: false,
    })

    return { data, isLoading }
}

export const qualityToVideoS3Key = (
    quality: ColorFinderFootageQuality,
    flight: VideoColorFinderFlight
) => {
    switch (quality) {
        case "medium":
            const videoNameMed = flight.job?.resolutions?.at(0)
            if (!videoNameMed) break
            return flight.job?.videoPath?.split("/").slice(0, -1).join("/") + `/${videoNameMed}`
        case "low":
            const videoNameLow = flight.job?.resolutions?.at(1)
            if (!videoNameLow) break
            return flight.job?.videoPath?.split("/").slice(0, -1).join("/") + `/${videoNameLow}`
    }

    return flight.job?.videoPath
}

const useColorFinderVideoUrl = (flight: VideoColorFinderFlight) => {
    const quality = useLocalStorageValue("VCF-QLTY-PRFRNC")
    const { getLocalVideo } = useUniversalControl()
    const { videoUrl } = getLocalVideo(flight.id)
    const rawKey = qualityToVideoS3Key(quality, flight)
    const s3Key = rawKey ? toDemoKey(rawKey, !!flight.demoId) : rawKey

    const { data, isLoading } = useS3VideoUrl(s3Key, !!flight.demoId)

    const isLocal = !!videoUrl

    return { data: isLocal ? videoUrl : data, isLoading: isLocal ? false : isLoading }
}

const useColorFinderImagesUrl = (flight: ImageColorFinderFlight) => {
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) ?? ""
    const projectId = useAppSelector(selectActivatedFireId) ?? ""
    const quality = useLocalStorageValue("VCF-QLTY-PRFRNC")

    const localImages = useAppSelector<Record<string, ColorFinderState> | undefined>(
        selectAllColorFinderImageUploads()
    )

    const hasLocalImages = !!(
        localImages && Object.values(localImages).some((image) => image.flightId === flight.id)
    )

    const [remoteUrls, setRemoteUrls] = useState<string[]>([])

    useEffect(() => {
        if (hasLocalImages) return
        let cancelled = false
        Promise.all(
            sortColorFinderImages(flight.images).map((image) => {
                const useLow = quality !== "high" && image.hasLowQuality
                const path = useLow
                    ? rgbDownscaledImagePath(workspaceId, projectId, flight.id, image.id)
                    : rgbImagePath(workspaceId, projectId, flight.id, image.id)
                return fileManagementApi.getDownloadUrl(path)
            })
        ).then((urls) => {
            if (!cancelled) setRemoteUrls(urls)
        })
        return () => {
            cancelled = true
        }
    }, [hasLocalImages, flight.images, workspaceId, projectId, flight.id, quality])

    const localIdsKey = hasLocalImages
        ? sortColorFinderImages(Object.values(localImages!))
              .filter((image) => image.flightId === flight.id)
              .map((local) => local.id)
              .join(",")
        : ""

    const localUrls = useMemo(
        () =>
            localIdsKey === ""
                ? []
                : localIdsKey.split(",").map((id) => URL.createObjectURL(stagedFiles[id])),
        [localIdsKey]
    )

    useEffect(() => {
        return () => {
            localUrls.forEach((url) => URL.revokeObjectURL(url))
        }
    }, [localUrls])

    if (hasLocalImages) {
        return localUrls
    }

    return remoteUrls
}

const resampleToFrames = (metadata: VideoMetadata, numFrames: number | undefined) =>
    !numFrames || metadata.length === 0
        ? metadata
        : Array.from(
              { length: numFrames },
              (_, frame) => metadata[Math.floor((frame * metadata.length) / numFrames)]
          )

const fetchMetadata = async (
    flight: ImageColorFinderFlight | VideoColorFinderFlight,
    { localImages }: { localImages: ColorFinderState[] | undefined }
) => {
    if (isVideoFlight(flight)) {
        const local = localVideoMap[flight.id]
        if (!!local) {
            const localIndex = local.colorData?.at(-1)
            return (
                local.metadata &&
                resampleToFrames(local.metadata, localIndex && buildColorIndex(localIndex).length)
            )
        }

        const metadataPath = flight.job?.metadataPath
        if (!metadataPath) return
        const s3Key = !!flight?.demoId ? `public/sample-data/${metadataPath}` : metadataPath
        const metadata = await fileManagementApi
            .fetchFile(s3Key, { isDemo: !!flight.demoId })
            .then((res) => res.arrayBuffer())
            .then((b) => decode(b) as VideoMetadata)
        return resampleToFrames(metadata, flight.job?.numFrames)
    } else {
        if (localImages?.length) {
            return localImages.map<VideoMetadata[number]>((image) => [
                image.longitude!,
                image.latitude!,
                image.height!,
                image.pitch!,
                image.yaw!,
                image.roll!,
                image.fov!,
                image.altitude!,
            ])
        }

        const metadata: VideoMetadata = sortColorFinderImages(flight.images).map((image) => [
            image.longitude,
            image.latitude,
            image.imageHeight ?? 0,
            image.pitch ?? 0,
            image.yaw ?? 0,
            image.roll ?? 0,
            image.fov,
            image.altitude ?? 0,
        ])
        return metadata
    }
}

export const useColorFinderMetadata = (
    flight: ImageColorFinderFlight | VideoColorFinderFlight | undefined
) => {
    const imageUploads = useAppSelector(selectUploadsForSet(flight?.id ?? "")) as ColorFinderState[]
    const { data, isLoading } = useSWR(
        ["metadata", flight?.id],
        () => flight && fetchMetadata(flight, { localImages: imageUploads })
    )

    return { data, isLoading }
}

export const getStartPosition = (flight: ImageColorFinderFlight | VideoColorFinderFlight) => {
    if (isVideoFlight(flight)) {
        return flight.startingPosition
            ? [flight.startingPosition.longitude, flight.startingPosition.latitude]
            : undefined
    } else {
        const image = flight.images?.at(0)
        return image ? [image.longitude, image.latitude] : undefined
    }
}

export const isVideoFlight = (
    flight: ImageColorFinderFlight | VideoColorFinderFlight
): flight is VideoColorFinderFlight => !get(flight, "images")

export const isLocalColorFinderFlight = (
    flight: ImageColorFinderFlight | VideoColorFinderFlight
) => {
    if (isVideoFlight(flight)) {
        return !flight.job
    } else {
        return !!localImageColorDataMap[flight.id] && flight.images.length === 0
    }
}

export const isV1ColorFinderVideoFlight = (flight: VideoColorFinderFlight) => {
    return !flight.job?.cumulativeColorIndex?.byteLength
}

export const isV1ColorFinderImageFlight = (flight: ImageColorFinderFlight) => {
    return (
        !flight.cumulativeColorIndex || flight.images.some((image) => !!image.colorData.byteLength)
    )
}

export const canOpenColorFinderFlight = (
    flight: ImageColorFinderFlight | VideoColorFinderFlight
): {
    result: boolean
    status?: GlobalNotificationStatus
    reason?: string
    notificationDuration?: number
} => {
    if (isVideoFlight(flight)) {
        return canOpenVideoColorFinderFlight(flight)
    } else {
        return canOpenImageColorFinderFlight(flight)
    }
}

const canOpenVideoColorFinderFlight = (flight: VideoColorFinderFlight) => {
    return { result: !!flight.job || !!localVideoMap[flight.id] }
}

const canOpenImageColorFinderFlight = (flight: ImageColorFinderFlight) => {
    if (isV1ColorFinderImageFlight(flight)) {
        return { result: true }
    }

    if (localImageColorDataMap[flight.id]) {
        return { result: true }
    }

    if (!!flight.anomalyPath) {
        return { result: true }
    }

    const lastImage = flight.images.findLast((image) => image.uploadedAt !== undefined)

    if (!lastImage) {
        return {
            result: false,
            reason: "No images found.",
            status: GlobalNotificationStatus.default,
        }
    }

    const lastImageTime = dateFromNumber(lastImage.uploadedAt ?? 0).getTime()
    const oneMinuteMillis = 1000 * 60
    const timeSinceLastUpload = Date.now() - lastImageTime

    if (timeSinceLastUpload <= oneMinuteMillis) {
        return {
            result: false,
            reason: "Processing color data...",
            status: GlobalNotificationStatus.default,
            notificationDuration: 8000,
        }
    } else {
        return {
            result: false,
            reason: "Unable to view color data. The upload was interrupted and needs to be retried.",
        }
    }
}

export const flightHasResolutions = (flight: ImageColorFinderFlight | VideoColorFinderFlight) => {
    if (isVideoFlight(flight)) {
        return !!flight.job?.resolutions.length
    } else {
        return flight.images.some((image) => image.hasLowQuality)
    }
}

export const isVideoFrameMetadataGeoValid = (frameMetadata: VideoMetadata[number] | undefined) => {
    if (!frameMetadata) return false

    return !(isNaN(frameMetadata[0]) || isNaN(frameMetadata[1]) || isNaN(frameMetadata[4]))
}

export const sortColorFinderImages = <T extends { takenAt?: number }>(images: T[]) =>
    images.toSorted((a, b) => (a.takenAt ?? 0) - (b.takenAt ?? 0))

export { useColorFinderVideoUrl, useColorFinderImagesUrl }
